var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { PickupModelMessage, PickupModelResponseMessage } from "@messages/attachment/AttachmentMessage";
import { worldToLocalPosition } from "flavr-common";
import { Quaternion } from "flavr-math";
import { InteractionHandler } from "../../Interactions";
import { PickupAction } from "./PickupAction";
import { modelPool } from "@modules/model";
import { getControllerPickupTargetUid } from "@modules/inputs/controller";
/**
 * Handles picking up a model and dropping it off. A model can either use an PickupFacet or directly an InteractionFacet.
 * The difference is that a Model with a PickupFacet can be picked up only by a controller. Whereas the InteractionFacet
 * can specify the new (temporary) parent of the pickup action.
 *
 * A PickupFacet can be constructed like this:
 *
 * ```js
 * // PickupFacet(useOffset, grabbableOffset, dropOffMode, dropOffLayers, cloneModel)
 * const offset = { position: [0, 0, 0], orientation: [0, 0, 0, 1], scale: [1, 1, 1] }
 * new PickupFacet(true, offset, DropOffMode.PREVIOUS_POSITION, undefined, false)
 * ```
 *
 * The respective InteractionFacet would look something like this:
 *
 * ```js
 * new InteractionFacet({
 *  onClick: [new PickupAction(controllerUID, {
 *    srcModelUid: uid,
 *    cloneModel: false,
 *    offset: {
 *      position,
 *      orientation,
 *      scale
 *    },
 *    dropOffMode: DropOffMode.PREVIOUS_POSITION,
 *    dropOffLayers: undefined, // can only be used with DropOffMode.DROP_OFF_AREA
 *  })]
 * })
 * ```
 */
export class PickupInteractionHandler extends InteractionHandler {
    constructor() {
        super(PickupAction.typestatic, [], [PickupAction.typestatic], []);
    }
    getActionDescription(params) {
        var _a;
        if (PickupAction.is(params)) {
            return (_a = params.description) !== null && _a !== void 0 ? _a : "";
        }
        return "";
    }
    performAction(params, context) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (!PickupAction.is(params))
                return;
            try {
                const newParentUID = (_a = params.parentUid) !== null && _a !== void 0 ? _a : (_b = context.controller) === null || _b === void 0 ? void 0 : _b.uid;
                const newModelUID = params.params.srcModelUid;
                if (newModelUID === undefined) {
                    console.warn('[interaction] Model cannot be attached because its uid is undefined');
                    return;
                }
                let offset = params.params.offset;
                if (params.params.useOffsetPriorToPickup && context.controller !== undefined) {
                    const model = modelPool.getModel(newModelUID);
                    // Resolve the actual parent: grip-space child in VR, controller model in desktop
                    const pickupTargetUID = getControllerPickupTargetUid(context.controller.uid);
                    const pickupTargetModel = modelPool.getModel(pickupTargetUID);
                    if (model !== undefined && pickupTargetModel !== undefined) {
                        const { position: modelPos, orientation: modelOri } = model.getWorldTransformComponents();
                        const { orientation: parentOri } = pickupTargetModel.getWorldTransformComponents();
                        const parentWorldMat = pickupTargetModel.getWorldTransform();
                        // Use full inverse world matrix so scale is handled correctly
                        const localPos = worldToLocalPosition(modelPos, parentWorldMat);
                        // Local orientation: parent inverse * model orientation
                        const parentOriInv = new Quaternion(parentOri.x, parentOri.y, parentOri.z, parentOri.w).invert();
                        const localOri = parentOriInv.clone().multiply(modelOri);
                        offset = {
                            position: [localPos.x, localPos.y, localPos.z],
                            orientation: [localOri.x, localOri.y, localOri.z, localOri.w],
                            scale: [1, 1, 1],
                        };
                    }
                }
                yield this.connection.askByMessageType(new PickupModelMessage(newModelUID, {
                    controllerUid: newParentUID,
                    offset,
                    dropOffMode: params.params.dropOffMode,
                    dropOffLayers: params.params.dropOffLayers,
                    dropOffButton: params.params.dropOffButton,
                    createTemporaryModel: params.params.cloneModel,
                    deleteOnDropOff: params.params.deleteOnDropOff,
                    keepSelectionActiveInVR: params.params.keepSelectionActiveInVR,
                    showDropOffIcon: params.params.showDropOffIcon
                }), PickupModelResponseMessage);
            }
            catch (error) {
                console.error("Error in PickupInteractionHandler: ", error);
            }
        });
    }
}
